// CompnayProfile.tsx
import React, { useState, useEffect, useRef } from "react";
import { useAppSelector, useAppDispatch } from "hooks/store";
import api from "config/api";
import { SketchPicker } from "react-color";
import { toast } from "react-toastify";
import { setSettings } from "store/slices/main.slice";

const convertNumberToHex = (num: number): string => {
  if (num === 0) return "#000";
  return `#${num.toString(16).padStart(6, "0")}`;
};

const convertHexToNumber = (hex: string) => parseInt(hex.replace("#", "0x"));
const MAX_FILE_SIZE = 2 * 1024 * 1024;

const CompanyProfile: React.FC = () => {
  const [newLogo, setNewLogo] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);

  const { settings, companyData } = useAppSelector(({ main }) => main);
  const dispatch = useAppDispatch();

  const [themeColor, setThemeColor] = useState("#000");
  const [itemPriceColor, setItemPriceColor] = useState("#000");
  const [itemColor, setItemColor] = useState("#000");
  const [deptColor, setDeptColor] = useState("#000");
  const [themeSelColor, setThemeSelColor] = useState("#000");
  const [headerColor, setHeaderColor] = useState("#000");
  const [itemPriceBgColor, setItemPriceBgColor] = useState("#000");

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Header Text Color state
  const [headerTextColor, setHeaderTextColor] = useState("#000");

  // Banner states (same pattern as Logo)
  const [newBanner, setNewBanner] = useState<File | null>(null);
  const [bannerPreviewUrl, setBannerPreviewUrl] = useState<string | undefined>(
    undefined
  );
  // === END SAMPATH ===

  const initialThemeColorRef = useRef("#000");
  const initialItemPriceColorRef = useRef("#000");
  const initialItemColorRef = useRef("#000");
  const initialDeptColorRef = useRef("#000");
  const initialThemeSelColorRef = useRef("#000");
  const initialHeaderSelColorRef = useRef("#000");
  const initialItemPriceBGSelColorRef = useRef("#000");

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // Track initial values for new settings
  const initialHeaderTextColorRef = useRef("#000");

  // Track initial logo url too (for correct load after refresh when backend returns only fileName)
  const initialLogoUrlRef = useRef<string | undefined>(undefined);

  // Track initial banner url too (for correct load after refresh when backend returns only fileName)
  const initialBannerUrlRef = useRef<string | undefined>(undefined);
  // === END SAMPATH ===

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // FIX: normalize double slashes in absolute URLs (example: https://domain.com//Upload/x.png)
  const normalizeAbsoluteUrl = (url?: string) => {
    if (!url) return undefined;
    const s = String(url).trim();
    if (!s) return undefined;

    if (!/^https?:\/\//i.test(s)) return s;

    try {
      const u = new URL(s);
      u.pathname = u.pathname.replace(/\/{2,}/g, "/");
      return u.toString();
    } catch {
      return s;
    }
  };

  // Cache-buster for images
  const withCacheBust = (url?: string) => {
    const fixed = normalizeAbsoluteUrl(url);
    if (!fixed) return undefined;
    const joiner = fixed.includes("?") ? "&" : "?";
    return `${fixed}${joiner}v=${Date.now()}`;
  };

  // === ADDED BY SAMPATH – 2026-01-29 ===
  // FIX: get best Upload root from API-returned URLs (banner/logo), so we never build wrong domain
  const getPreferredUploadRoot = () => {
    const candidates = [
      (settings as any)?.headerBannerUrl,
      companyData?.logoUrl,
    ].filter(Boolean) as string[];

    for (const c of candidates) {
      if (/^https?:\/\//i.test(c)) {
        try {
          const u = new URL(c);
          // ensure ends with /Upload
          return `${u.origin}/Upload`;
        } catch {
          // ignore
        }
      }
    }

    // fallback: build from api base (old behavior)
    const base = (api as any)?.defaults?.baseURL || "";
    const root = String(base).replace(/\/api\/?$/i, "");
    return String(root).replace(/\/+$/g, "") + "/Upload";
  };

  // FIX: compare by filename (backend saves/returns sometimes filename, sometimes full URL)
  const getFileNameOnly = (raw?: string) => {
    if (!raw) return "";
    const s = String(raw).trim();
    if (!s) return "";
    try {
      if (/^https?:\/\//i.test(s)) {
        const u = new URL(s);
        const parts = u.pathname.split("/").filter(Boolean);
        return parts[parts.length - 1] || "";
      }
    } catch {
      // ignore
    }
    const parts = s.split("/").filter(Boolean);
    return parts[parts.length - 1] || s;
  };
  // === END SAMPATH ===

  // Resolve upload url
  const resolveUploadUrl = (raw?: string) => {
    if (!raw) return undefined;
    const trimmed = String(raw).trim();
    if (!trimmed) return undefined;

    // if already absolute url -> use as-is (but normalize slashes)
    if (/^https?:\/\//i.test(trimmed)) return normalizeAbsoluteUrl(trimmed);

    // === ADDED BY SAMPATH – 2026-01-29 ===
    // FIX: if backend/Redux gives only fileName (headerbanner.png),
    // build using preferred Upload root from correct domain (testorder.onlinefoodordering.ca)
    const uploadRoot = getPreferredUploadRoot();
    const cleanFile = trimmed.replace(/^\/+/g, "");
    const lower = cleanFile.toLowerCase();

    // if already contains Upload/ or uploads/, just attach to origin properly
    if (
      lower.startsWith("upload/") ||
      lower.startsWith("uploads/") ||
      lower.includes("/upload/") ||
      lower.includes("/uploads/")
    ) {
      // attach to origin of uploadRoot
      try {
        const u = new URL(uploadRoot);
        return normalizeAbsoluteUrl(`${u.origin}/${cleanFile}`);
      } catch {
        return normalizeAbsoluteUrl(`${uploadRoot}/${cleanFile}`);
      }
    }

    return normalizeAbsoluteUrl(`${uploadRoot}/${cleanFile}`);
    // === END SAMPATH ===
  };

  useEffect(() => {
    const logoRaw = companyData?.logoUrl as any as string | undefined;
    const logoResolved = resolveUploadUrl(logoRaw);
    setPreviewUrl(withCacheBust(logoResolved));
    initialLogoUrlRef.current = logoRaw;

    const bannerRaw = (settings as any)?.headerBannerUrl as string | undefined;
    const bannerResolved = resolveUploadUrl(bannerRaw);
    setBannerPreviewUrl(withCacheBust(bannerResolved));
    initialBannerUrlRef.current = bannerRaw;

    const initialThemeColor = convertNumberToHex(settings.themeColor || 0x000);
    const initialItemPriceColor = convertNumberToHex(
      settings.itemPriceColor || 0x000
    );
    const initialItemColor = convertNumberToHex(settings.itemColor || 0x000);
    const initialDeptColor = convertNumberToHex(settings.deptColor || 0x000);
    const initialThemeSelColor = convertNumberToHex(
      settings.themeSelColor || 0x000
    );

    setThemeColor(initialThemeColor);
    setItemPriceColor(initialItemPriceColor);
    setItemColor(initialItemColor);
    setDeptColor(initialDeptColor);
    setThemeSelColor(initialThemeSelColor);

    initialThemeColorRef.current = initialThemeColor;
    initialItemPriceColorRef.current = initialItemPriceColor;
    initialItemColorRef.current = initialItemColor;
    initialDeptColorRef.current = initialDeptColor;
    initialThemeSelColorRef.current = initialThemeSelColor;

    const initialHeaderColor = convertNumberToHex(
      (settings as any).headerColor || 0x000
    );
    const initialItemPriceBgColor = convertNumberToHex(
      (settings as any).itemPriceBgColor || 0x000
    );

    setHeaderColor(initialHeaderColor);
    setItemPriceBgColor(initialItemPriceBgColor);

    initialHeaderSelColorRef.current = initialHeaderColor;
    initialItemPriceBGSelColorRef.current = initialItemPriceBgColor;

    const initialHeaderTextColor = convertNumberToHex(
      (settings as any).headerTextColor || 0x000
    );
    setHeaderTextColor(initialHeaderTextColor);
    initialHeaderTextColorRef.current = initialHeaderTextColor;
  }, [companyData, settings]);

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      if (file.size > MAX_FILE_SIZE) {
        toast.error("File size exceeds 2MB limit. Please upload a smaller file.");
        return;
      }

      setNewLogo(file);
      const fileUrl = URL.createObjectURL(file);
      setPreviewUrl(fileUrl);
    }
  };

  const handleBannerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const file = e.target.files[0];

      if (file.size > MAX_FILE_SIZE) {
        toast.error("File size exceeds 2MB limit. Please upload a smaller file.");
        return;
      }

      setNewBanner(file);
      const fileUrl = URL.createObjectURL(file);
      setBannerPreviewUrl(fileUrl);
    }
  };

  const uploadLogo = async (): Promise<string | null> => {
    if (!newLogo) return null;

    const formData = new FormData();
    formData.append("formFile", newLogo);

    try {
      const response = await api.post("/api/FileUtility/UploadLogo", formData);
      const responseData = response.data?.data?.data?.[0];

      if (responseData?.status === "OK") {
        toast.success("Logo uploaded successfully");
        return responseData.fileName;
      } else if (responseData?.status === "ERROR") {
        toast.error(responseData.errorMessage || "Failed to upload logo");
        return null;
      } else {
        toast.error("Unexpected response from the server");
        return null;
      }
    } catch (error) {
      toast.error("Failed to upload logo due to a network error");
      return null;
    }
  };

  const uploadBanner = async (): Promise<string | null> => {
    if (!newBanner) return null;

    const formData = new FormData();
    formData.append("formFile", newBanner);

    try {
      const response = await api.post("/api/FileUtility/UploadBanner", formData);
      const responseData = response.data?.data?.data?.[0];

      if (responseData?.status === "OK") {
        toast.success("Banner uploaded successfully");
        return responseData.fileName;
      } else if (responseData?.status === "ERROR") {
        toast.error(responseData.errorMessage || "Failed to upload banner");
        return null;
      } else {
        toast.error("Banner Upload - Unexpected response from the server");
        return null;
      }
    } catch (error) {
      toast.error("Failed to upload banner due to a network error");
      return null;
    }
  };

  const handleUpdate = async () => {
    setIsLoading(true);

    const logoUrl = await uploadLogo();
    if (!logoUrl && newLogo) {
      setIsLoading(false);
      return;
    }

    const bannerUrl = await uploadBanner();
    if (!bannerUrl && newBanner) {
      setIsLoading(false);
      return;
    }

    const savedLogoResolved = logoUrl ? resolveUploadUrl(logoUrl) : undefined;
    if (savedLogoResolved) {
      setPreviewUrl(withCacheBust(savedLogoResolved));
      setNewLogo(null);
    }

    // === ADDED BY SAMPATH – 2026-01-29 ===
    // FIX: store/display FULL banner URL (correct domain), while backend still receives fileName
    const savedBannerResolvedFull = bannerUrl ? resolveUploadUrl(bannerUrl) : undefined;
    if (savedBannerResolvedFull) {
      setBannerPreviewUrl(withCacheBust(savedBannerResolvedFull));
      setNewBanner(null);
    }
    // === END SAMPATH ===

    const hasThemeColorChanged = themeColor !== initialThemeColorRef.current;
    const hasItemPriceColorChanged =
      itemPriceColor !== initialItemPriceColorRef.current;
    const hasItemColorChanged = itemColor !== initialItemColorRef.current;
    const hasDeptColorChanged = deptColor !== initialDeptColorRef.current;
    const hasThemeSelColorChanged =
      themeSelColor !== initialThemeSelColorRef.current;
    const hasHeaderColorChanged =
      headerColor !== initialHeaderSelColorRef.current;
    const hasItemPriceBGColorChanged =
      itemPriceBgColor !== initialItemPriceBGSelColorRef.current;

    const hasHeaderTextColorChanged =
      headerTextColor !== initialHeaderTextColorRef.current;

    // === ADDED BY SAMPATH – 2026-01-29 ===
    // FIX: compare by filename only (URL vs fileName mismatch was causing wrong change detection)
    const hasBannerChanged =
      !!bannerUrl &&
      getFileNameOnly(bannerUrl) !== getFileNameOnly(initialBannerUrlRef.current);
    // === END SAMPATH ===

    if (
      hasThemeColorChanged ||
      hasItemPriceColorChanged ||
      hasItemColorChanged ||
      hasDeptColorChanged ||
      hasThemeSelColorChanged ||
      hasHeaderColorChanged ||
      hasItemPriceBGColorChanged ||
      hasHeaderTextColorChanged ||
      hasBannerChanged
    ) {
      const payload: any[] = [
        { Id: 0, Value: convertHexToNumber(themeColor), Name: "themeColor" },
        { Id: 0, Value: convertHexToNumber(itemPriceColor), Name: "itemPriceColor" },
        { Id: 0, Value: convertHexToNumber(itemColor), Name: "itemColor" },
        { Id: 0, Value: convertHexToNumber(deptColor), Name: "deptColor" },
        { Id: 0, Value: convertHexToNumber(themeSelColor), Name: "themeSelColor" },
        { Id: 0, Value: convertHexToNumber(headerColor), Name: "headerColor" },
        { Id: 0, Value: convertHexToNumber(itemPriceBgColor), Name: "itemPriceBgColor" },
        { Id: 0, Value: convertHexToNumber(headerTextColor), Name: "headerTextColor" },
      ];

      if (bannerUrl) {
        payload.push({
          Id: 0,
          Value: bannerUrl, // backend keeps fileName
          Name: "headerBannerUrl",
        });
      }

      api
        .post("/api/Option/AddUpdateSettings", payload)
        .then(() => {
          dispatch(
            setSettings({
              ...settings,
              themeColor: convertHexToNumber(themeColor),
              itemPriceColor: convertHexToNumber(itemPriceColor),
              itemColor: convertHexToNumber(itemColor),
              deptColor: convertHexToNumber(deptColor),
              themeSelColor: convertHexToNumber(themeSelColor),
              headerColor: convertHexToNumber(headerColor),
              itemPriceBgColor: convertHexToNumber(itemPriceBgColor),
              headerTextColor: convertHexToNumber(headerTextColor),

              // === ADDED BY SAMPATH – 2026-01-29 ===
              // FIX: keep FULL URL in Redux so UI never builds wrong domain
              ...(bannerUrl
                ? ({ headerBannerUrl: savedBannerResolvedFull || bannerUrl } as any)
                : {}),
              // === END SAMPATH ===
            } as any)
          );

          initialThemeColorRef.current = themeColor;
          initialItemPriceColorRef.current = itemPriceColor;
          initialItemColorRef.current = itemColor;
          initialDeptColorRef.current = deptColor;
          initialThemeSelColorRef.current = themeSelColor;
          initialHeaderSelColorRef.current = headerColor;
          initialItemPriceBGSelColorRef.current = itemPriceBgColor;

          initialHeaderTextColorRef.current = headerTextColor;

          // === ADDED BY SAMPATH – 2026-01-29 ===
          // FIX: store latest banner ref as FULL URL for stable UI, but comparisons use getFileNameOnly()
          if (bannerUrl) initialBannerUrlRef.current = savedBannerResolvedFull || bannerUrl;
          // === END SAMPATH ===

          toast.success("Settings updated successfully");
        })
        .catch(() => {
          toast.error("Something went wrong");
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
      toast.info("No changes to update");
    }
  };
          //Company Profile  Name change to Customization
  return (
    <div className="h-full w-full flex flex-col  p-4 overflow-y-auto ">
      <h2 className="text-2xl font-bold text-center text-gray-800">
        
        Company Profile
      </h2>

      {settings.showLogo !== 0 && (
        <div className="mt-8 flex flex-col items-center">
          {previewUrl && (
            <div className="flex justify-center items-center max-h-[200px] p-1 mb-6">
              <img
                src={previewUrl}
                alt="Company Logo"
                className="max-h-[200px] aspect-square rounded-3xl border-4 p-1 border-gray-200 object-cover"
                onError={(e) => {
                  const img = e.currentTarget;
                  const src = img.src || "";
                  if (src.includes("/Upload/") && !src.includes("/Uploads/")) {
                    img.src = src.replace("/Upload/", "/Uploads/");
                  }
                }}
              />
            </div>
          )}
          <label className="block text-lg font-semibold text-gray-600 mb-2">
            Upload New Logo
          </label>
          <input
            type="file"
            onChange={handleLogoChange}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
            accept="image/*"
          />
        </div>
      )}

      {/* === ADDED BY SAMPATH – 2026-01-29 === */}
      {/* Banner Upload UI (same process as Logo) */}
      <div className="mt-8 flex flex-col items-center">
        {bannerPreviewUrl && (
          <div className="w-full flex flex-col justify-center items-center max-h-[260px] p-1 mb-6">
            <img
              src={bannerPreviewUrl}
              alt="Header Banner"
              className="block max-h-[220px] w-full max-w-[900px] rounded-3xl border-4 p-1 border-gray-200 object-cover"
              crossOrigin="anonymous"
              referrerPolicy="no-referrer"
              loading="lazy"
              onError={(e) => {
                const img = e.currentTarget as HTMLImageElement;
                const src = img.src || "";

                const normalized = normalizeAbsoluteUrl(src);
                if (normalized && normalized !== src) {
                  img.src = normalized;
                  return;
                }

                if (src.includes("/Upload/") && !src.includes("/Uploads/")) {
                  img.src = src.replace("/Upload/", "/Uploads/");
                }
              }}
            />

            {/* Show current banner URL for debugging */}
            <div className="mt-2 text-xs text-gray-500 break-all text-center">
              {normalizeAbsoluteUrl(bannerPreviewUrl)}
            </div>
          </div>
        )}
        <label className="block text-lg font-semibold text-gray-600 mb-2">
          Upload Header Banner
        </label>
        <input
          type="file"
          onChange={handleBannerChange}
          className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-400"
          accept="image/*"
        />
      </div>
      {/* === END SAMPATH === */}

      <div className="mt-8">
        <h3 className="text-lg font-semibold text-gray-800">Customize Colors</h3>
        <div className="flex flex-wrap mt-4 gap-8">
          {[
            { label: "Theme Color", color: themeColor, setColor: setThemeColor },
            { label: "Item Price Color", color: itemPriceColor, setColor: setItemPriceColor },
            { label: "Item Color", color: itemColor, setColor: setItemColor },
            { label: "Department Color", color: deptColor, setColor: setDeptColor },
            { label: "Theme Selection Color", color: themeSelColor, setColor: setThemeSelColor },
            { label: "Header Color", color: headerColor, setColor: setHeaderColor },
            { label: "Header Text Color", color: headerTextColor, setColor: setHeaderTextColor },
            { label: "Item Price Background Color", color: itemPriceBgColor, setColor: setItemPriceBgColor },
          ].map(({ label, color, setColor }) => (
            <div className="flex flex-col items-center" key={label}>
              <label className="block text-lg font-semibold text-gray-600 mb-2">
                {label}
              </label>
              <SketchPicker
                color={color}
                onChangeComplete={(color) => setColor(color.hex)}
              />
            </div>
          ))}
        </div>
      </div>

      <div className="w-full flex justify-center mt-2 col-span-1 lg:col-span-2">
        <button
          onClick={handleUpdate}
          className="mt-8 w-full sm:w-auto px-6 py-3 bg-primary text-themeSelColor font-semibold rounded-lg shadow  transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          disabled={isLoading}
        >
          {isLoading ? "Updating..." : "Save Changes"}
        </button>
      </div>
    </div>
  );
};

export default CompanyProfile;
